import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton,
} from "@mui/material";

import { withAuth } from "../../hoc";
import { Video } from "../content/types";
import {
  SubscriptionThumbCard,
  VideoThumbCard,
} from "../../components/ContentCard";

import {
  GetDietRequestStates,
  GetMemberStates,
  GetMostSoldProgram,
  GetMostViewVideos,
  GetPurchaseStates,
  IDietRequestState,
  IMemberStats,
  IPurchaseState,
  ISoldProgramStates,
} from "../../services/StatesServices";
import { formatNumber } from "../../utils/commons";
import { grey, orange } from "@mui/material/colors";
import { Height } from "@mui/icons-material";

const Dashboard = () => {
  return (
    <Box
      sx={{
        maxHeight: "90vh",
        height: "90vh",
        padding: 3,
        scrollbarWidth: "none",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        flex: "1 1 auto",
        overflowY: "auto",
      }}
    >
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "#fff" }}>
          Dashboard
        </Typography>
      </Box>
      <Box display={"flex"} gap={3}>
        <MemberStatsCard />
        <PurchaseStats />
        <DietRequestCard />
      </Box>
      <Box display={"flex"} gap={3} flex={1}>
        <MostViewedVideosCard />
        <MostSoldProgramsCard />
      </Box>
    </Box>
  );
};

export default withAuth(Dashboard);

const MostViewedVideosCard = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [timeFrame, setTimeFrame] = useState("THIS_YEAR");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setVideos([]);
    fetchVideos();
  }, [timeFrame]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const videoData = await GetMostViewVideos({ timeFrame });
      setVideos(videoData);
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectOptions = [
    { value: "LAST_7DAYS", label: "Last 7 Days" },
    { value: "LAST_MONTH", label: "Last 30 Days" },
    { value: "LAST_3MONTHS", label: "Last 90 Days" },
    { value: "LAST_6MONTHS", label: "Last 6 Months" },
    { value: "THIS_YEAR", label: "This Year" },
  ];

  const handleTimeFrameChange = (event: any) => {
    setTimeFrame(event.target.value as string);
  };

  console.log("videos.length", !videos?.length, loading);
  return (
    <Box sx={cardContainerStyle}>
      <Box sx={cardHeaderStyle}>
        <Typography variant="h6" sx={{ fontSize: 20, color: "#fff" }}>
          Most Viewed Videos
        </Typography>
        <FormControl variant="outlined" sx={{ width: 200 }}>
          <Select
            value={timeFrame}
            disabled={loading}
            onChange={handleTimeFrameChange}
            sx={{ color: "#fff" }}
          >
            {selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Divider sx={dividerStyle} />
      <Box sx={cardContentStyle}>
        {!videos.length || loading
          ? Array.from({ length: 3 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))
          : Array.from({ length: 3 }).map((_, index) => {
              const video = videos[index];
              return (
                <Box key={index} sx={{ width: "32%" }}>
                  {video ? (
                    <>
                      <VideoThumbCard
                        srcUrl={video?.thumbnailUrl}
                        duration={video.duration}
                        width={"100%"}
                      />
                      <Typography
                        variant="body2"
                        sx={{ marginTop: 1, color: "#fff" }}
                      >
                        {video.title}
                      </Typography>
                      <Typography variant="body2">
                        <b style={{ color: "gray" }}>
                          {video.totalViews} views
                        </b>
                      </Typography>
                    </>
                  ) : (
                    <Box
                      sx={{
                        height: "70%",
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                      }}
                    />
                  )}
                </Box>
              );
            })}
      </Box>
    </Box>
  );
};

const SkeletonCard = () => {
  return (
    <Box
      sx={{
        // height: "65%",
        width: "32%",
        aspectRatio: "16/9",
        // border: "1px solid red",
        backgroundColor: "rgba(0, 0, 0, 0.1)",
      }}
    >
      <Skeleton
        // animation="wave"
        // variant="text"
        variant="rectangular"
        width={"100%"}
        height={"100%"}
      />
    </Box>
  );
};

const MostSoldProgramsCard = () => {
  const [subscriptions, setSubscriptions] = useState<ISoldProgramStates>();
  const [timeFrame, setTimeFrame] = useState("THIS_YEAR");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, [timeFrame]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const subscriptionData = await GetMostSoldProgram({ timeFrame });
      setSubscriptions(subscriptionData);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectOptions = [
    { value: "LAST_7DAYS", label: "Last 7 Days" },
    { value: "LAST_MONTH", label: "Last 30 Days" },
    { value: "LAST_3MONTHS", label: "Last 90 Days" },
    { value: "LAST_6MONTHS", label: "Last 6 Months" },
    { value: "THIS_YEAR", label: "This Year" },
  ];

  const handleTimeFrameChange = (event: any) => {
    setTimeFrame(event.target.value as string);
  };

  console.log("subscriptions", subscriptions);
  return (
    <Box sx={cardContainerStyle}>
      <Box sx={cardHeaderStyle}>
        <Typography variant="h6" sx={{ fontSize: 20, color: "#fff" }}>
          Most Sold Programs
        </Typography>
        <FormControl variant="outlined" sx={{ width: 200 }}>
          <Select
            value={timeFrame}
            onChange={handleTimeFrameChange}
            sx={{ color: "#fff" }}
            disabled={loading}
          >
            {selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Divider sx={dividerStyle} />
      <Box sx={cardContentStyle} width={"100%"}>
        {loading
          ? Array.from({ length: 4 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))
          : subscriptions &&
            subscriptions.programs.map((subscription, index) => (
              <Box key={index} sx={programCardStyle}>
                <SubscriptionThumbCard
                  srcUrl={
                    subscription.subscriptionDetails.thumbnailUrl as string
                  }
                  width={"100%"}
                />
                <Box sx={{ width: "100%", marginTop: 1 }}>
                  <Typography variant="body2" sx={{ color: "#fff" }}>
                    {subscription.subscriptionDetails.name}
                  </Typography>
                  <Typography variant="body2" style={{ color: "gray" }}>
                    <b>
                      Total Sold:{" "}
                      {formatNumber(subscription.subCount as number)}
                    </b>
                  </Typography>
                </Box>
              </Box>
            ))}
      </Box>
    </Box>
  );
};

const MemberStatsCard = () => {
  const [stats, setStats] = useState<IMemberStats | null>(null); // Use null or undefined to indicate initial absence of data
  const [timeFrame, setTimeFrame] = useState<string>("THIS_YEAR"); // Default time frame
  const [isLoading, setIsloading] = useState<boolean>(false);
  useEffect(() => {
    fetchMemberStats();
  }, [timeFrame]);

  const fetchMemberStats = async () => {
    setIsloading(true);
    try {
      const response = await GetMemberStates({ timeFrame });
      setStats(response as IMemberStats);
      setIsloading(false);
    } catch (error) {
      console.error("Error fetching member stats:", error);
    }
  };

  const handleTimeFrameChange = (event: any) => {
    setTimeFrame(event.target.value as string);
  };

  const selectOptions = [
    { value: "LAST_7DAYS", label: "Last 7 Days" },
    { value: "LAST_MONTH", label: "Last 30 Days" },
    { value: "LAST_3MONTHS", label: "Last 90 Days" },
    { value: "LAST_6MONTHS", label: "Last 6 Months" },
    { value: "THIS_YEAR", label: "This Year" },
  ];

  return (
    <StatCard
      title="Member Joining Stats"
      total={`Total Joined : ${stats && formatNumber(stats.total)}`} // Use conditional rendering to handle initial state
      stats={[
        { label: "Gym Joins", value: stats?.totalGymMembers as number },
        { label: "Online Joins", value: stats?.totalOnlineMembers as number },
        { label: "Subscriber Joins", value: stats?.totalSubscribers as number },
        { label: "Visitor Joins", value: stats?.totalVisitors as number },
      ]}
      selectOptions={selectOptions}
      handleTimeFrameChange={handleTimeFrameChange}
      selectedTimeFrame={timeFrame}
      loading={isLoading}
    />
  );
};
const DietRequestCard = () => {
  const [stats, setStats] = useState<IDietRequestState | null>(null); // Use null or undefined to indicate initial absence of data
  const [timeFrame, setTimeFrame] = useState<string>("THIS_YEAR"); // Default time frame
  const [isLoading, setIsloading] = useState<boolean>(false);
  useEffect(() => {
    setStats(null);
    fetchDietRequestStats();
  }, [timeFrame]);

  const fetchDietRequestStats = async () => {
    try {
      setIsloading(true);
      const response = await GetDietRequestStates({
        timeFrame,
      });

      setStats(response as IDietRequestState);
      setIsloading(false);
    } catch (error) {
      console.error("Error fetching member stats:", error);
      setIsloading(false);
    }
  };

  const handleTimeFrameChange = (event: any) => {
    setTimeFrame(event?.target?.value as string);
  };

  const selectOptions = [
    { value: "LAST_7DAYS", label: "Last 7 Days" },
    { value: "LAST_MONTH", label: "Last 30 Days" },
    { value: "LAST_3MONTHS", label: "Last 90 Days" },
    { value: "LAST_6MONTHS", label: "Last 6 Months" },
    { value: "THIS_YEAR", label: "This Year" },
  ];
  return (
    <StatCard
      title="Diet Requests"
      total={`Total : ${formatNumber(stats?.total as number)}`}
      stats={[
        { label: "Open", value: stats?.open as number },
        { label: "Addressed", value: stats?.addressed as number },
      ]}
      selectOptions={selectOptions}
      handleTimeFrameChange={handleTimeFrameChange}
      selectedTimeFrame={timeFrame}
      loading={isLoading}
    />
  );
};

const PurchaseStats = () => {
  const [stats, setStats] = useState<IPurchaseState | null>(null); // Use null or undefined to indicate initial absence of data
  const [timeFrame, setTimeFrame] = useState<string>("THIS_YEAR"); // Default time frame
  const [isLoading, setIsloading] = useState<boolean>(false);
  useEffect(() => {
    fetchPurchaseStats();
  }, [timeFrame]);

  const fetchPurchaseStats = async () => {
    try {
      setIsloading(true);
      const response = await GetPurchaseStates({ timeFrame });
      setStats(response as IPurchaseState);
      setIsloading(false);
    } catch (error) {
      console.error("Error fetching member stats:", error);
    }
  };

  const handleTimeFrameChange = (event: any) => {
    setTimeFrame(event.target.value as string);
  };

  const selectOptions = [
    { value: "LAST_7DAYS", label: "Last 7 Days" },
    { value: "LAST_MONTH", label: "Last 30 Days" },
    { value: "LAST_3MONTHS", label: "Last 90 Days" },
    { value: "LAST_6MONTHS", label: "Last 6 Months" },
    { value: "THIS_YEAR", label: "This Year" },
  ];

  return (
    <StatCard
      title="Purchase Stats"
      total={`Total Sold : ${formatNumber(stats?.totalSold as number)} `}
      tatalFloat={`₹ ${formatNumber(stats?.totalAmount as number)}`}
      stats={[
        { label: "Gym Members", value: stats?.gymMemberships as number },
        { label: "Online Members", value: stats?.onlineMemberships as number },
        { label: "Other Programs", value: stats?.otherPrograms as number },
      ]}
      selectOptions={selectOptions}
      handleTimeFrameChange={handleTimeFrameChange}
      selectedTimeFrame={timeFrame}
      loading={isLoading}
    />
  );
};

const cardContainerStyle = {
  height: "100%",
  background: "linear-gradient(145deg, #424242, #303030)",
  display: "flex",
  flexDirection: "column",
  // justifyContent: "center",
  alignItems: "center",
  borderRadius: 2,
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  padding: 3,
  flex: 1,
  color: "white",
};

const cardHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  marginBottom: 2,
};

const dividerStyle = {
  width: "100%",
  marginBottom: 2,
  backgroundColor: "white",
};

const cardContentStyle = {
  display: "flex",
  flexDirection: "row",
  overflowX: "auto",
  gap: 2,
  // border: "1px solid gray",

  width: "100%",
};

const programCardStyle = {
  justifyContent: "space-between",
  width: "33%",
  borderRadius: 2,
};

const selectStyle = {
  color: "white",
  borderColor: "white",
};

const StatCard = ({
  title,
  total,
  stats,
  selectOptions,
  handleTimeFrameChange,
  selectedTimeFrame,
  tatalFloat,
  loading = false, // Add loading prop to indicate when data is loading
}: {
  title: string;
  total: any;
  stats: { label: string; value: string | number }[];
  selectOptions: { value: string; label: string }[];
  handleTimeFrameChange?: (event: any) => void;
  selectedTimeFrame?: string;
  tatalFloat?: any;
  loading?: boolean;
}) => (
  <Box sx={cardContainerStyle}>
    <Box sx={cardHeaderStyle}>
      <Typography variant="h6">{title}</Typography>
      <FormControl variant="outlined" sx={{ width: 200 }}>
        <Select
          value={selectedTimeFrame}
          onChange={handleTimeFrameChange}
          sx={selectStyle}
          disabled={loading} // Disable select when loading
        >
          {selectOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
    <Divider sx={dividerStyle} />
    <Box sx={{ width: "100%", marginBottom: 2 }}>
      {loading ? (
        <Skeleton animation="wave" variant="text" width="40%" />
      ) : (
        <Typography variant="h6" sx={{ color: "#fff" }}>
          {total}{" "}
          <span
            style={{ textAlign: "right", float: "right", color: orange[300] }}
          >
            {tatalFloat}
          </span>
        </Typography>
      )}
    </Box>
    <Divider sx={dividerStyle} />

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {loading
        ? // Display skeleton placeholders when loading
          Array.from(Array(3).keys()).map((index) => (
            <Box key={index} sx={{ textAlign: "center" }}>
              <Skeleton
                animation="wave"
                variant="text"
                width={100}
                // height={50}
              />
              <Skeleton
                animation="wave"
                variant="text"
                width={60}
                // height={20}
              />
            </Box>
          ))
        : // Display actual stats when not loading
          stats.map((stat, index) => (
            <Box key={index} sx={{ textAlign: "center" }}>
              <Typography variant="h6" sx={{ color: grey[400] }}>
                <b>{formatNumber(stat?.value as number) || 0}</b>
              </Typography>
              <Typography variant="body2" sx={{ color: "gray" }}>
                <b> {stat?.label}</b>
              </Typography>
            </Box>
          ))}
    </Box>
  </Box>
);
